import {
  BillingProfileStatus,
  PaymentType,
  SyncSource,
} from '../../components/ClientBillingProfileManagement/types'
import { StripePaymentMethodDto } from './invoice-delivery.dto'

/** A Stripe customer on file for the profile's client. */
export interface StripeCustomerDto {
  stripeCustomerId: string
  /** Name of the Stripe Connect account this customer belongs to */
  stripeAccount: string
  isActive: boolean
  description: string | null
  defaultStripePaymentMethodId: string | null
  /** Mirrored from Stripe; null when never synced */
  stripePaymentMethods: StripePaymentMethodDto[] | null
  createdAt: string
  updatedAt: string
}

export interface BillingProfileRecord {
  id: number
  clientName: string
  clientPublicKey: string | null
  toEmails: string[]
  ccEmails: string[]
  paymentType: PaymentType | null
  dueDay: number | null
  /** Plan start of the HubSpot deal invoices are priced from; null until a sync has run */
  planEffectiveDate: string | null
  status: BillingProfileStatus
  createdAt: string
  updatedAt: string
  createdBy: string
  /** When a HubSpot sync last wrote this profile; null until one has run */
  lastSyncedAt: string | null
  lastSyncSource: SyncSource | null
  /** Active and inactive, newest first */
  stripeCustomers?: StripeCustomerDto[]
}

/** List columns the server can sort by; values match the EPS query. */
export enum BillingProfileSortField {
  ClientName = 'clientName',
  PaymentType = 'paymentType',
  PlanEffectiveDate = 'planEffectiveDate',
  DueDay = 'dueDay',
  LastSyncedAt = 'lastSyncedAt',
}

export type SortOrder = 'asc' | 'desc'

export interface GetBillingProfilesRequestDto {
  search?: string
  status?: BillingProfileStatus
  paymentType?: PaymentType
  clientPublicKey?: string
  page?: number
  limit?: number
  excludeTestClients?: boolean
  /** Newest first when unset; empty values sort last. */
  sortBy?: BillingProfileSortField
  sortOrder?: SortOrder
}

export interface GetBillingProfilesResponseDto {
  data: BillingProfileRecord[]
  total: number
  limit: number
  offset: number
}

export interface CreateBillingProfileRequestDto {
  clientName: string
  clientPublicKey?: string
  toEmails: string[]
  ccEmails?: string[]
  paymentType?: PaymentType
  dueDay?: number
}

export interface UpdateStripeCustomerRequestDto {
  stripeCustomerId: string
  /** Omit to leave untouched */
  isActive?: boolean
  /** Omit to leave untouched; null clears it */
  description?: string | null
}

export interface UpdateBillingProfileRequestDto {
  clientName?: string
  clientPublicKey?: string
  toEmails?: string[]
  ccEmails?: string[]
  paymentType?: PaymentType
  dueDay?: number
  /** All-or-nothing: one rejected customer fails the whole update */
  stripeCustomers?: UpdateStripeCustomerRequestDto[]
}

export interface ToggleBillingProfileStatusRequestDto {
  status: BillingProfileStatus
  reason?: string
}

export interface GetBillingProfileStatsRequestDto {
  excludeTestClients?: boolean
  /** Same search as the list, so each option counts the rows it would show. */
  search?: string
  paymentType?: PaymentType
}

export interface BillingProfileStatsDto {
  total: number
  active: number
  inactive: number
}

export interface BulkUploadRowError {
  row: number
  message: string
}

export interface BulkUploadResultDto {
  created: number
  updated: number
  skipped: number
  errors: BulkUploadRowError[]
}

export interface LocalProfileOptionDto {
  /** null when the client has no billing profile yet */
  clientBillingProfileId: number | null
  clientPublicKey: string
  companyName: string
  currentToEmails: string[]
  paymentType: string | null
  dueDay: number | null
}

export interface HubspotSyncUpdateItemDto {
  /** null when no billing profile exists yet — one will be created on submit */
  clientBillingProfileId: number | null
  /** null for legacy billing profiles with no client link; required for auto-create */
  clientPublicKey: string | null
  toEmails: string[]
  /** Subset of toEmails cleared for funding portal access to this company. Omit to leave every written address eligible. */
  accessEmails?: string[]
  /** Optional on auto-create. For existing profiles it is applied when sent and left unchanged when omitted. */
  paymentType?: string | null
  /** Day of the month, 1-31. Null to clear; omit to leave unchanged. */
  dueDay?: number | null
  /** Plan start from the qualifying HubSpot deal (YYYY-MM-DD). Omit to leave unchanged — HubSpot owns it, so it is never cleared from here. */
  planEffectiveDate?: string
}

export interface HubspotSyncSubmitDto {
  updates: HubspotSyncUpdateItemDto[]
}

export interface HubspotSyncSubmitAppliedDto {
  clientBillingProfileId: number
  clientPublicKey: string | null
}

export interface HubspotSyncSubmitFailureDto {
  clientBillingProfileId: number | null
  clientPublicKey: string | null
  reason: string
}

export interface HubspotSyncSubmitResultDto {
  applied: HubspotSyncSubmitAppliedDto[]
  failed: HubspotSyncSubmitFailureDto[]
}

// ── New 3-part auto-sync result (POST /hubspot-sync/run) ──────────────────────

export interface HubspotSyncNewItemDto {
  hubspotCompanyId: string
  hubspotCompanyName: string
  /** clientPublicKey resolved from platform_client_public_key — null when no match found */
  clientPublicKey: string | null
  hubspotContactEmails: string[]
  /** How many of hubspotContactEmails came from the "Send Funding Report" label;
   *  0 means HubSpot has no customer contacts even when internal ones exist */
  sendFundingReportEmailCount: number
  paymentType: string | null
  dueDay: number | null
  /** Plan start of the deal that qualified this company; null only when the deal search degraded */
  planEffectiveDate: string | null
}

export interface HubspotSyncUpdatedItemDto {
  hubspotCompanyId: string
  hubspotCompanyName: string
  clientBillingProfileId: number
  clientPublicKey: string
  companyName: string
  /** Proposed final recipient list (the HubSpot set) — applied on submit */
  toEmails: string[]
  /** Emails to be added (in HubSpot, not currently on the profile) */
  addedEmails: string[]
  /** Emails to be removed (currently on the profile, not in HubSpot) */
  removedEmails: string[]
  /** How many of toEmails came from the "Send Funding Report" label; 0 means
   *  HubSpot has no customer contacts even when internal ones exist */
  sendFundingReportEmailCount: number
  /** Current payment type on the profile (WIRE/STRIPE or null) */
  currentPaymentType: string | null
  /** Proposed payment type from the Active deal; differs when the deal changed it */
  proposedPaymentType: string | null
  /** Plan start stored on the profile, or null when never synced */
  currentPlanEffectiveDate: string | null
  /** Plan start from the qualifying deal; differs when the plan year moved */
  proposedPlanEffectiveDate: string | null
}

export interface HubspotSyncNoChangesItemDto {
  hubspotCompanyId: string
  hubspotCompanyName: string
  clientBillingProfileId: number
  clientPublicKey: string
  companyName: string
}

export interface HubspotSyncWarningItemDto {
  hubspotCompanyId: string
  hubspotCompanyName: string
  reason: 'duplicate_client_public_key' | 'unknown_client_public_key'
}

export interface HubspotSyncResultDto {
  newItems: HubspotSyncNewItemDto[]
  updatedItems: HubspotSyncUpdatedItemDto[]
  noChanges: HubspotSyncNoChangesItemDto[]
  localProfiles: LocalProfileOptionDto[]
  errorItems: HubspotSyncWarningItemDto[]
}
